import { useMemo } from "react";
import { deskCard, deskEyebrow, deskMuted, deskPrimaryBtn, deskSecondaryBtn } from "./salesDeskStyles";
import {
  SALES_DESK_HOME_ROW_COUNTS,
  salesDeskBuildingsInProgress,
  salesDeskHomeSlice,
  type SalesDeskHomeRowCount,
} from "./salesDeskHomeLayout";
import type { SalesDeskLead } from "./salesDeskLead";
import type { SalesDeskOrder } from "./salesDeskOrderFilter";

const chipClass =
  "rounded-full border border-[#dce3dc] bg-white px-4 py-2 text-sm font-semibold text-[#142d2d] dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100";

export function SalesDeskHome({
  onStart,
  onOpenLead,
  onConfigure,
  onLot,
  onOrders,
  onOpenSale,
  onReceipts,
  locationLabel,
  leads,
  leadsLoading,
  orders,
  ordersLoading,
  receiptReviewCount,
  receiptsLoading,
  showLeads,
  showBuilds,
  rowCount,
  onShowLeads,
  onShowBuilds,
  onRowCount,
}: {
  onStart: () => void;
  onOpenLead: (lead: SalesDeskLead) => void;
  onConfigure: () => void;
  onLot: () => void;
  onOrders: () => void;
  onOpenSale: (id: string) => void;
  onReceipts: () => void;
  locationLabel: string;
  leads: SalesDeskLead[];
  leadsLoading: boolean;
  orders: SalesDeskOrder[];
  ordersLoading: boolean;
  receiptReviewCount: number;
  receiptsLoading: boolean;
  showLeads: boolean;
  showBuilds: boolean;
  rowCount: SalesDeskHomeRowCount;
  onShowLeads: (shown: boolean) => void;
  onShowBuilds: (shown: boolean) => void;
  onRowCount: (count: SalesDeskHomeRowCount) => void;
}) {
  const place = locationLabel ? locationLabel.toUpperCase() : "SALES DESK";
  const visibleLeads = useMemo(() => salesDeskHomeSlice(leads, rowCount), [leads, rowCount]);
  const buildings = useMemo(
    () => salesDeskHomeSlice(salesDeskBuildingsInProgress(orders), rowCount),
    [orders, rowCount],
  );

  return (
    <div>
      <p className={deskEyebrow}>{locationLabel ? `${place} · SALES DESK` : "SALES DESK"}</p>
      <h1 className="mt-2 text-[38px] font-semibold leading-[1.08] tracking-[-1.4px]">
        From the first hello
        <br />
        to their backyard.
      </h1>
      <p className={`mt-3 ${deskMuted}`}>The right building. A clear price. One next step.</p>
      <div className="mt-6 flex flex-col gap-3 sm:flex-row">
        <button type="button" onClick={onStart} className={`${deskPrimaryBtn} uppercase tracking-[1px] sm:min-w-[220px]`}>
          Start a sale →
        </button>
        <button type="button" onClick={onOrders} className={`${deskSecondaryBtn} sm:min-w-[220px]`}>
          Look up an order
        </button>
      </div>

      <div className="mt-8 flex flex-wrap items-center justify-between gap-3 rounded-[13px] bg-[#e7eef2] px-4 py-4 dark:bg-zinc-800">
        <p className="text-[18px] font-semibold leading-tight">Pick a path.</p>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={onConfigure} className={chipClass}>
            1 · Custom build
          </button>
          <button type="button" onClick={onLot} className={chipClass}>
            2 · Lot sale
          </button>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-4 text-sm">
        <label className="inline-flex items-center gap-2">
          <input type="checkbox" checked={showLeads} onChange={(event) => onShowLeads(event.target.checked)} />
          Incoming leads
        </label>
        <label className="inline-flex items-center gap-2">
          <input type="checkbox" checked={showBuilds} onChange={(event) => onShowBuilds(event.target.checked)} />
          Buildings in progress
        </label>
        <label className="inline-flex items-center gap-2">
          Rows
          <select
            aria-label="Rows"
            value={rowCount}
            onChange={(event) => onRowCount(Number(event.target.value) as SalesDeskHomeRowCount)}
            className="rounded-md border border-[#dce3dc] bg-white px-2 py-1 dark:border-zinc-700 dark:bg-zinc-900"
          >
            {SALES_DESK_HOME_ROW_COUNTS.map((count) => (
              <option key={count} value={count}>
                {count}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {showLeads ? (
          <section className={deskCard}>
            <h2 className="text-[21px] font-semibold tracking-[-0.4px]">Incoming leads</h2>
            <button type="button" onClick={onReceipts} className={`${deskSecondaryBtn} mt-4`}>
              IdeaRoom receipts
              {receiptsLoading ? null : (
                <span className="ml-2 rounded-full bg-[#eef1e7] px-2.5 py-1 text-[11px] font-semibold text-[#164b43] dark:bg-zinc-800 dark:text-emerald-200">
                  {receiptReviewCount} need review
                </span>
              )}
            </button>
            {leadsLoading ? <p className={`mt-4 ${deskMuted}`}>Loading leads…</p> : null}
            {!leadsLoading && leads.length === 0 ? (
              <p className={`mt-4 ${deskMuted}`}>No leads yet. Start a sale when someone walks up.</p>
            ) : null}
            <div className="mt-2">
              {visibleLeads.map((lead) => (
                <button
                  key={lead.id}
                  type="button"
                  onClick={() => onOpenLead(lead)}
                  className="flex w-full items-center gap-3 border-b border-[#dce3dc] py-4 text-left last:border-b-0 dark:border-zinc-700"
                >
                  <span className="grid h-[42px] w-[42px] shrink-0 place-items-center rounded-full bg-[#eef1e7] text-sm font-semibold dark:bg-zinc-800">
                    {lead.initials}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold">{lead.name}</span>
                    <span className="block text-xs text-[#687772] dark:text-zinc-400">{lead.need}</span>
                  </span>
                  {lead.source ? (
                    <span className="rounded-full bg-[#e4edf2] px-2.5 py-1 text-[11px] text-[#315d75] dark:bg-zinc-800 dark:text-sky-200">
                      {lead.source}
                    </span>
                  ) : null}
                </button>
              ))}
            </div>
          </section>
        ) : null}
        {showBuilds ? (
          <section className={deskCard}>
            <h2 className="text-[21px] font-semibold tracking-[-0.4px]">Buildings in progress</h2>
            {ordersLoading ? <p className={`mt-4 ${deskMuted}`}>Loading sales…</p> : null}
            {!ordersLoading && buildings.length === 0 ? (
              <p className={`mt-4 ${deskMuted}`}>No open sales yet.</p>
            ) : null}
            <div className="mt-2">
              {buildings.map((order) => (
                <button
                  key={order.id}
                  type="button"
                  onClick={() => onOpenSale(order.id)}
                  className="block w-full border-b border-[#dce3dc] py-4 text-left last:border-b-0 dark:border-zinc-700"
                >
                  <span className="block text-sm font-semibold">{order.customer}</span>
                  <span className="mt-1 block text-xs text-[#687772] dark:text-zinc-400">
                    {[order.model, order.serial, order.fulfillment].filter(Boolean).join(" · ") || order.saleStatus}
                  </span>
                </button>
              ))}
            </div>
            <button type="button" onClick={onOrders} className="mt-4 font-semibold text-[#164b43] dark:text-emerald-300">
              Look up orders →
            </button>
          </section>
        ) : null}
      </div>
    </div>
  );
}
